// The Schedule of Work's photos: the area's photos as every screen lists them
// (area-photos.ts: its rows, its older list, this device's), less any whose file
// is gone from SharePoint (DD, 2026-10-14: left out, and said). Each is read
// from the device if it's there, else fetched from SharePoint (needs signal),
// else the device's thumbnail — and scaled for the PDF.
import { getPhotoContent } from '../api';
import { shownPhotos, type PhotoSources } from '../area-photos';
import type { DevicePhoto } from '../device-db';
import type { PhotoRef } from './model';

/** Longest side in the PDF, and JPEG quality: sharp enough on A4, small enough to send. */
const MAX_SIDE_PX = 1200;
const JPEG_QUALITY = 0.7;

export interface LoadedPhoto {
  dataUrl: string;
  width: number;
  height: number;
}

/** Every photo of the area, once each, in name order (Walls 01, Walls 02 … General 01). */
export function areaPhotos(area: { id: string; fields: Record<string, unknown> }, sources: PhotoSources): PhotoRef[] {
  return shownPhotos(area, sources)
    .filter((p) => !p.missing)
    .map((p) => ({ id: p.id, areaId: area.id, surfaceId: p.surfaceId, name: p.fileName, direction: p.direction }));
}

async function toBlob(source: string | Blob): Promise<Blob> {
  return typeof source === 'string' ? (await fetch(source)).blob() : source;
}

/** Scale to fit MAX_SIDE_PX, the camera's orientation applied, as a JPEG data URL. */
async function scaled(blob: Blob): Promise<LoadedPhoto> {
  const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
  const scale = Math.min(1, MAX_SIDE_PX / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return { dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY), width, height };
}

/** One photo for the PDF; null when it can't be had at all. */
async function loadOne(ref: PhotoRef, device: DevicePhoto | undefined): Promise<LoadedPhoto | null> {
  try {
    if (device?.image) return await scaled(device.image);
    if (navigator.onLine) return await scaled(await getPhotoContent(ref.areaId, ref.id));
  } catch {
    // Fall through to the thumbnail.
  }
  return device ? scaled(await toBlob(device.thumbnail)).catch(() => null) : null;
}

/** Load the photos the document uses, a few at a time, reporting progress. */
export async function loadPhotos(refs: readonly PhotoRef[], onDevice: readonly DevicePhoto[], progress: (done: number, total: number) => void): Promise<Map<string, LoadedPhoto>> {
  const deviceById = new Map(onDevice.map((p) => [p.id, p]));
  const loaded = new Map<string, LoadedPhoto>();
  const PARALLEL = 4;
  let done = 0;
  for (let i = 0; i < refs.length; i += PARALLEL) {
    const batch = refs.slice(i, i + PARALLEL);
    const results = await Promise.all(batch.map((r) => loadOne(r, deviceById.get(r.id))));
    batch.forEach((r, j) => {
      const photo = results[j];
      if (photo) loaded.set(r.id, photo);
    });
    done += batch.length;
    progress(done, refs.length);
  }
  return loaded;
}
